import { Buffer } from "buffer";
import { sha256 } from "@noble/hashes/sha256";
import { ComputeBudgetProgram, PublicKey, SystemProgram, Transaction, TransactionInstruction } from "@solana/web3.js";
import bs58 from "bs58";
import { assertWalletMessage } from "./walletMessage.js";
// One canonical codec for browser, native and backend approvals. Legacy nonce,
// zero-priority-fee and 200k-CU messages are accepted only to recover saved bytes.
const TOKEN_PROGRAM_ID = new PublicKey("TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA");
const ASSOCIATED_TOKEN_PROGRAM_ID = new PublicKey("ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL");
const getAssociatedTokenAddressSync = (mint, owner) => PublicKey.findProgramAddressSync([owner.toBuffer(), TOKEN_PROGRAM_ID.toBuffer(), mint.toBuffer()], ASSOCIATED_TOKEN_PROGRAM_ID)[0];
const createAssociatedTokenAccountIdempotentInstruction = (payer, address, owner, mint, tokenProgram, associatedProgram) => new TransactionInstruction({ programId: associatedProgram, data: Buffer.from([1]), keys: [meta(payer, true, true), meta(address, true), meta(owner), meta(mint), meta(SystemProgram.programId), meta(tokenProgram)] });
export const RESOURCE_CLAIM_CHUNK_MAX_ITEMS = 2;
export const RESOURCE_CLAIM_CHUNK_COMPUTE_UNITS = 400_000;
export const MATERIAL_CLAIM_PRIORITY_MICROLAMPORTS = 100_000;
export function validateResourceClaimChunkManifest(input) {
    if (input.version !== "OWNER_CHUNK_V1" || !Array.isArray(input.items) || input.items.length < 1
        || input.items.length > RESOURCE_CLAIM_CHUNK_MAX_ITEMS || !validId(input.preparationKey))
        throw invalid("material_chunk_invalid");
    for (const key of [input.wallet, input.program, input.refineryAuthority])
        publicKey(key);
    if (input.wallet === input.refineryAuthority)
        throw invalid("material_chunk_authority_invalid");
    const claims = new Set(), resources = new Set(), mints = new Set();
    for (const item of input.items) {
        if (!item || !validId(item.claimId) || claims.has(item.claimId) || resources.has(item.resourceId) || mints.has(item.mint)
            || !/^[a-z][a-z0-9_]{0,63}$/.test(item.resourceId) || !["RECORDED_V1", "OWNER_ATOMIC_V1"].includes(item.deliveryMode)
            || !/^[1-9][0-9]{0,19}$/.test(item.amountBaseUnits) || BigInt(item.amountBaseUnits) > 18446744073709551615n)
            throw invalid("material_chunk_item_invalid");
        publicKey(item.mint);
        claims.add(item.claimId);
        resources.add(item.resourceId);
        mints.add(item.mint);
    }
}
/** Shared production construction and confirmed-message verification. The
 * caller supplies only persisted entitlements and mints from the chain registry. */
export function resourceClaimChunkInstructions(manifest) {
    validateResourceClaimChunkManifest(manifest);
    const program = publicKey(manifest.program), owner = publicKey(manifest.wallet);
    const config = pda(program, "economy_config_v3"), registry = pda(program, "asset_registry_v3");
    const mintAuthority = pda(program, "economy_mint_authority_v1");
    const instructions = [];
    for (const item of manifest.items) {
        const mint = publicKey(item.mint), destination = getAssociatedTokenAddressSync(mint, owner);
        const emission = pda(program, "resource_emission_v3", hash(item.resourceId));
        const claim = pda(program, "resource_claim_v3", hash(item.claimId));
        instructions.push(createAssociatedTokenAccountIdempotentInstruction(owner, destination, owner, mint, TOKEN_PROGRAM_ID, ASSOCIATED_TOKEN_PROGRAM_ID));
        if (item.deliveryMode === "OWNER_ATOMIC_V1") {
            const amount = Buffer.alloc(8);
            amount.writeBigUInt64LE(BigInt(item.amountBaseUnits));
            instructions.push(new TransactionInstruction({ programId: program, keys: [
                    meta(owner, true, true), meta(publicKey(manifest.refineryAuthority), false, true), meta(config), meta(registry),
                    meta(emission, true), meta(claim, true), meta(mint, true), meta(destination, true), meta(mintAuthority), meta(TOKEN_PROGRAM_ID), meta(SystemProgram.programId),
                ], data: Buffer.concat([discriminator("record_and_claim_resource"), hash(item.claimId), amount]) }));
        }
        else {
            instructions.push(new TransactionInstruction({ programId: program, keys: [meta(owner, true, true), meta(config), meta(registry),
                    meta(emission, true), meta(claim, true), meta(mint, true), meta(destination, true), meta(mintAuthority),
                    meta(pda(program, "idempotency_v3", owner.toBuffer(), hash(manifest.preparationKey)), true), meta(TOKEN_PROGRAM_ID), meta(SystemProgram.programId),
                ], data: Buffer.concat([discriminator("claim_processed_resource"), hash(manifest.preparationKey)]) }));
        }
    }
    return instructions;
}
export function buildResourceClaimChunk(manifest, blockhash, validity) {
    const transaction = assembleResourceClaimChunk(manifest, blockhash, validity);
    if (transaction.serialize({ requireAllSignatures: false, verifySignatures: false }).length > 1232)
        throw invalid("material_chunk_packet_too_large");
    return transaction;
}
function assembleResourceClaimChunk(manifest, blockhash, validity) {
    if (validity && (validity.kind !== "DURABLE_NONCE_V1" || validity.preparationKey !== manifest.preparationKey || validity.nonceValue !== blockhash
        || validity.authority === manifest.wallet || !Number.isSafeInteger(validity.observedSlot) || validity.observedSlot < 0))
        throw invalid("material_chunk_nonce_invalid");
    publicKey(blockhash);
    const transaction = new Transaction({ feePayer: publicKey(manifest.wallet), recentBlockhash: blockhash });
    if (validity)
        transaction.add(SystemProgram.nonceAdvance({ noncePubkey: publicKey(validity.nonceAddress), authorizedPubkey: publicKey(validity.authority) }));
    transaction.add(ComputeBudgetProgram.setComputeUnitLimit({ units: RESOURCE_CLAIM_CHUNK_COMPUTE_UNITS }));
    transaction.add(ComputeBudgetProgram.setComputeUnitPrice({ microLamports: MATERIAL_CLAIM_PRIORITY_MICROLAMPORTS }));
    transaction.add(...resourceClaimChunkInstructions(manifest));
    return transaction;
}
/** Check every instruction and every signature, not just a matching instruction
 * somewhere in a transaction. This also validates confirmed and retained finalized RPC evidence. */
export function validateSignedResourceClaimChunk(encoded, manifest, validity) {
    try {
        const bytes = Buffer.from(encoded, "base64");
        if (bytes.length > 1232 || bytes.toString("base64") !== encoded)
            throw invalid("material_chunk_wire_invalid");
        const transaction = Transaction.from(bytes);
        // The received packet is already size-checked. Compile its exact expected
        // message once; serializing a second packet adds no validation evidence.
        if (!matchesResourceClaimChunk(transaction, manifest, validity))
            throw invalid("material_chunk_message_conflict");
        if (!transaction.verifySignatures())
            throw invalid("material_chunk_signature_invalid");
        return bs58.encode(transaction.signature);
    }
    catch (cause) {
        if (cause instanceof MaterialClaimError)
            throw cause;
        throw invalid("material_chunk_wire_invalid");
    }
}
function publicKey(value) {
    try {
        const result = new PublicKey(value);
        if (result.toBase58() !== value)
            throw new Error("noncanonical");
        return result;
    }
    catch {
        throw invalid("material_chunk_public_key_invalid");
    }
}
const validId = (value) => typeof value === "string" && /^[A-Za-z0-9_-]{8,128}$/.test(value);
const hash = (value) => Buffer.from(sha256(new TextEncoder().encode(value)));
const discriminator = (value) => hash(`global:${value}`).subarray(0, 8);
const sharedAddresses = new Map();
const sharedSeeds = new Set(["economy_config_v3", "asset_registry_v3", "economy_mint_authority_v1", "resource_emission_v3"]);
function pda(program, name, ...seeds) {
    // Only immutable program/resource addresses are shared. Owner, claim and
    // idempotency addresses still derive per request; no wallet state is cached.
    if (!sharedSeeds.has(name))
        return PublicKey.findProgramAddressSync([Buffer.from(name), ...seeds], program)[0];
    const key = `${program.toBuffer().toString("hex")}\0${name}\0${seeds.map(seed => seed.toString("hex")).join("\0")}`;
    const prior = sharedAddresses.get(key);
    if (prior)
        return new PublicKey(prior);
    const result = PublicKey.findProgramAddressSync([Buffer.from(name), ...seeds], program)[0];
    if (sharedAddresses.size >= 128)
        sharedAddresses.delete(sharedAddresses.keys().next().value);
    sharedAddresses.set(key, result.toBuffer());
    return result;
}
const meta = (pubkey, isWritable = false, isSigner = false) => ({ pubkey, isWritable, isSigner });
export class MaterialClaimError extends Error {
    code;
    constructor(code) {
        super(code);
        this.code = code;
    }
}
const invalid = (code) => new MaterialClaimError(code);
function chunkMessageMatches(expected, transaction) {
    try {
        assertWalletMessage(expected.compileMessage(), transaction.compileMessage());
        return true;
    }
    catch {
        return false;
    }
}
export function matchesResourceClaimChunk(transaction, manifest, validity) {
    return [RESOURCE_CLAIM_CHUNK_COMPUTE_UNITS, 200_000].some(units => {
        const expected = assembleResourceClaimChunk(manifest, transaction.recentBlockhash, validity);
        expected.instructions[validity ? 1 : 0] = ComputeBudgetProgram.setComputeUnitLimit({ units });
        if (chunkMessageMatches(expected, transaction))
            return true;
        const priceIndex = validity ? 2 : 1;
        expected.instructions[priceIndex] = ComputeBudgetProgram.setComputeUnitPrice({ microLamports: 0 });
        if (chunkMessageMatches(expected, transaction))
            return true;
        if (validity) {
            expected.instructions.splice(priceIndex, 1);
            return chunkMessageMatches(expected, transaction);
        }
        return false;
    });
}
